import { useTranslation } from 'next-i18next'
import { useRef } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import SelectionButton from '~/bootcamp/Activities/components/SelectionButton'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import useFractionTypes from '~/bootcamp/Activities/Fractions/Bootcamp1/FractionTypes/useFractionTypes'
import { FractionTypeNames } from '~/bootcamp/Activities/Fractions/Fractions.type'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

function FractionTypes({
  answerState,
  actionButtonsFC,
  autofocus = true,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const {
    numerator,
    denominator,
    quotient,
    formValid,
    selectedAnswer,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useFractionTypes({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
  })

  const { t } = useTranslation('bootcamp')
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEmitInstance(
    onInstanceChange,
    denominator
      ? `${quotient ? `${quotient} ` : ''}\\frac{${numerator}}{${denominator}}; ` +
          'options: proper fraction, improper fraction, mixed number'
      : null,
  )

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex items-center space-x-1">
          {!!quotient && <span className="text-2xl">{quotient}</span>}
          <div className="flex flex-col items-center">
            <span className="text-2xl">{numerator}</span>
            <Divider />
            <span className="text-2xl">{denominator}</span>
          </div>
        </div>
      </FractionsActivityWrapper>
      <div className="flex justify-between space-x-2 w-full">
        <SelectionButton
          className="p-2 flex-1"
          isSelected={selectedAnswer === FractionTypeNames.PROPER}
          isWrong={selectedAnswer === FractionTypeNames.PROPER && answerState === false}
          key={FractionTypeNames.PROPER}
          onClick={onInputChange.bind(null, FractionTypeNames.PROPER)}
        >
          <span>{t('fractions.properFraction')}</span>
        </SelectionButton>
        <SelectionButton
          className="p-2 flex-1"
          isSelected={selectedAnswer === FractionTypeNames.IMPROPER}
          isWrong={selectedAnswer === FractionTypeNames.IMPROPER && answerState === false}
          key={FractionTypeNames.IMPROPER}
          onClick={onInputChange.bind(null, FractionTypeNames.IMPROPER)}
        >
          <span>{t('fractions.improperFraction')}</span>
        </SelectionButton>
        <SelectionButton
          className="p-2 flex-1"
          isSelected={selectedAnswer === FractionTypeNames.MIXED}
          isWrong={selectedAnswer === FractionTypeNames.MIXED && answerState === false}
          key={FractionTypeNames.MIXED}
          onClick={onInputChange.bind(null, FractionTypeNames.MIXED)}
        >
          <span>{t('fractions.mixedFraction')}</span>
        </SelectionButton>
      </div>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: goToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionTypes
